import { classifyCapgoReason, isContentReason } from './capgoReason';
import { probeDelta, shouldUseManifest, type DeltaProbeResult } from './deltaProbe';
import { describeUpdateFailure } from './failureDetail';
import type { ManifestEntry } from './updateCheck';

export const BACKOFF_BASE_MS = 2_000;
export const BACKOFF_CAP_MS = 30_000;
export const PROGRESSIVE_HINT_TTL_MS = 7 * 24 * 3_600_000;
export const PROGRESSIVE_BACKOFF_MS = 1_000;
export const PROGRESSIVE_PROBE_TIMEOUT_MS = 5_000;

// Full jitter: `random(0, min(cap, base * 2^attempt))`. An OTA release fails fleet-wide
// simultaneously, so un-jittered backoff would produce synchronised retry clusters against our own
// storage.
export const computeBackoffMs = (
  attempt: number,
  random: () => number = Math.random,
): number => {
  const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
  return Math.floor(random() * ceiling);
};

export interface UpdateRunnerDeps {
  download: (opts: {
    url: string;
    version: string;
    manifest?: ManifestEntry[];
    checksum?: string;
  }) => Promise<{ id: string }>;
  set: (opts: { id: string }) => Promise<void>;
  reload: () => Promise<void>;
  onProgress: (percent: number) => void;
  // Awaited so a failure event that waits on diagnostics is logged before the next attempt can
  // start, apply and swap the WebView.
  onAttemptFailed: (
    attempt: number,
    error: unknown,
    telemetry: RunUpdateResult,
  ) => Promise<void> | void;
  // Fires after the download succeeded and BEFORE `set()`. The ordering is load-bearing: on Android
  // `set()` swaps the WebView and only resolves once the NEW bundle calls notifyAppReady, so nothing
  // after `await set()` ever runs there (0 Android successes in 30 days against thousands on iOS).
  // Semantics are therefore "downloaded and handed to set()"; a `set()` rejection still reaches
  // `onAttemptFailed`, so a device that failed to apply shows a failure row too. The caller must
  // also send this event past the analytics batch queue — the swap follows within milliseconds.
  onApplied: (telemetry: RunUpdateResult) => void;
  wait: (ms: number) => Promise<void>;
  shouldContinue: () => boolean;
  getMissingBundleFiles: (options: { manifest: ManifestEntry[]; version: string }) => Promise<{
    total: number;
    missingCount: number;
    reusableCount: number;
    missing: ManifestEntry[];
  }>;
  now: () => number;
  isProgressiveTrigger: (error: unknown) => boolean;
  cleanupFailedBundles?: (version: string) => Promise<void>;
  readProgressiveHint: () => { version: string; until: number } | null;
  writeProgressiveHint: (hint: { version: string; until: number } | null) => void;
  reportError: (message: string, error?: unknown) => void;
  onProbe?: () => void;
}

type RunnableDecision =
  | {
      kind: 'download';
      url: string;
      version: string;
      manifest?: ManifestEntry[];
      checksum?: string;
    }
  | {
      kind: 'apply_installed';
      bundleId: string;
      version: string;
      url?: string;
      checksum?: string;
    };

export type UpdateMode = 'manifest' | 'zip' | 'manifest_fell_back' | 'manifest_progressive';
export type FallbackReason =
  | 'probe_failed'
  | 'delta_too_large'
  | 'manifest_failed'
  | 'zip_timeout'
  | 'remembered';

export interface RunUpdateResult {
  // Absent (not `'zip'`) when the successful attempt applied an already-installed bundle from disk
  // — zero bytes transferred, so labelling it `'zip'` would inflate the zip share of the telemetry
  // mode-mix.
  updateMode?: UpdateMode;
  fallbackReason?: FallbackReason;
  probe?: DeltaProbeResult;
  attempt: number;
  attemptDurationMs: number;
  filesMissingAfter?: number;
}

// Retries until success or until `shouldContinue()` goes false.
export const runUpdate = async (
  decision: RunnableDecision,
  deps: UpdateRunnerDeps,
): Promise<RunUpdateResult> => {
  let attempt = 0;
  let current: RunnableDecision = decision;

  // `undefined` means "attempt with the zip"; a defined array means "attempt with this manifest,
  // checksum forced to ''".
  let activeManifest: ManifestEntry[] | undefined;
  // `apply_installed` applies a bundle already on disk — zero bytes transferred, so it must not
  // report 'zip' (see RunUpdateResult.updateMode).
  // Cast: closures (the guard) reassign it, which control-flow narrowing cannot see.
  let updateMode = (decision.kind === 'apply_installed' ? undefined : 'zip') as UpdateMode | undefined;
  let fallbackReason: FallbackReason | undefined;
  let probe: DeltaProbeResult | undefined;
  let manifestFailureStreak = 0;
  let progressiveExhausted = false;
  let progressiveBaseline: number | undefined;
  let noProgressStreak = 0;
  let unknownStreak = 0;
  let probeInFlight = false;
  let progressiveDisabled = false;

  // Any exception in a progressive step lands here: the gate session keeps today's zip loop and the
  // failure is reported once, so a bug in the new path can never strand a device.
  const disableProgressive = (error: unknown): void => {
    if (progressiveDisabled) {
      return;
    }
    progressiveDisabled = true;
    if (updateMode === 'manifest_progressive') {
      activeManifest = undefined;
      updateMode = 'manifest_fell_back';
      fallbackReason = 'manifest_failed';
    }
    try {
      deps.writeProgressiveHint(null);
    } catch {
      // best-effort: a throwing hint store must not escape or be reported twice
    }
    try {
      deps.reportError('OTA progressive fallback disabled', error);
    } catch {
      // a throwing reporter must not escape the guard
    }
  };

  const guarded = <T>(run: () => T, fallback: T): T => {
    if (progressiveDisabled) {
      return fallback;
    }
    try {
      return run();
    } catch (error) {
      disableProgressive(error);
      return fallback;
    }
  };

  // Probed once, before the loop — a probe costs a full SHA-256 of every candidate file (see
  // deltaProbe.ts), so it must not repeat once the loop's ordinary retries begin.
  if (decision.kind === 'download' && decision.manifest) {
    const probeResult = await probeDelta(
      { getMissingBundleFiles: deps.getMissingBundleFiles, now: deps.now },
      decision.manifest,
      decision.version,
    );
    if (probeResult === null) {
      fallbackReason = 'probe_failed';
    } else {
      probe = probeResult;
      if (!shouldUseManifest(probeResult)) {
        fallbackReason = 'delta_too_large';
      } else {
        activeManifest = decision.manifest;
        updateMode = 'manifest';
      }
    }
  }
  progressiveBaseline = probe?.filesMissing;

  if (decision.kind === 'download' && decision.manifest) {
    const rememberedManifest = decision.manifest;
    guarded(() => {
      const hint = deps.readProgressiveHint();
      if (hint && hint.until <= deps.now()) {
        deps.writeProgressiveHint(null);
      } else if (hint && hint.version === decision.version) {
        activeManifest = rememberedManifest;
        updateMode = 'manifest_progressive';
        fallbackReason = 'remembered';
      }
    }, undefined);
  }

  // Resolves undefined on throw or timeout; resolve() is idempotent, so a late result of an
  // abandoned call cannot change anything.
  const raceTimeout = <T>(run: () => Promise<T>): Promise<T | undefined> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => resolve(undefined), PROGRESSIVE_PROBE_TIMEOUT_MS);
      const settle = (value: T | undefined) => {
        clearTimeout(timer);
        resolve(value);
      };
      try {
        run().then(settle, () => settle(undefined));
      } catch {
        settle(undefined);
      }
    });

  // One re-probe at a time: a timed-out probe keeps hashing natively, so a new one would pile on.
  // A skipped re-probe is an unknown result.
  const raceProbe = async (
    manifest: ManifestEntry[],
    version: string,
  ): Promise<number | undefined> => {
    if (probeInFlight) {
      return undefined;
    }
    probeInFlight = true;
    const release = () => {
      probeInFlight = false;
    };
    const result = await raceTimeout(() => {
      let pending: ReturnType<UpdateRunnerDeps['getMissingBundleFiles']>;
      try {
        pending = deps.getMissingBundleFiles({ manifest, version });
        pending.then(release, release);
      } catch (error) {
        release();
        throw error;
      }
      return pending;
    });
    return result?.missingCount;
  };

  const runCleanup = async (version: string): Promise<void> => {
    const cleanup = deps.cleanupFailedBundles;
    if (!cleanup || progressiveDisabled) {
      return;
    }
    await raceTimeout(() => cleanup(version));
    guarded(() => deps.onProbe?.(), undefined);
  };

  // A remembered start never passes through the catch path, which is where cleanup normally runs.
  if (updateMode === 'manifest_progressive') {
    await runCleanup(decision.version);
  }

  for (;;) {
    // Reset the bar at the start of every attempt (including the first) so a retry never strands
    // the UI at the previous attempt's stalled percentage.
    deps.onProgress(0);
    const attemptStartedAt = deps.now();
    // Only a download() failure should count against the manifest's failure streak — a set()
    // rejection after a successful manifest download says nothing about the manifest itself, and
    // must not be mislabelled as `fallback_reason: 'manifest_failed'`.
    let downloadFailed = false;
    try {
      let bundleId: string;
      if (current.kind === 'apply_installed') {
        bundleId = current.bundleId;
      } else {
        // `checksum: ''` accompanies `manifest` on EVERY manifest attempt: 8.51.14 iOS throws AND
        // deletes the bundle on a populated checksum next to a manifest; 8.52+ ignores it. A zip
        // attempt carries the real checksum, which 8.52+ requires and 8.51.14 verifies.
        const downloadOpts =
          activeManifest !== undefined
            ? {
                url: current.url,
                version: current.version,
                manifest: activeManifest,
                checksum: '',
              }
            : {
                url: current.url,
                version: current.version,
                ...(current.checksum !== undefined && { checksum: current.checksum }),
              };
        try {
          bundleId = (await deps.download(downloadOpts)).id;
        } catch (downloadError) {
          downloadFailed = true;
          throw downloadError;
        }
      }

      const appliedMode = updateMode;
      const appliedReason = fallbackReason;

      // Before set(), deliberately — see UpdateRunnerDeps.onApplied.
      const attemptDurationMs = deps.now() - attemptStartedAt;
      const applied = {
        updateMode: appliedMode,
        fallbackReason: appliedReason,
        probe,
        attempt,
        attemptDurationMs,
      };
      deps.onApplied(applied);
      await deps.set({ id: bundleId });
      // Never awaited for completion: reload() tears down the webview and its promise never
      // settles. On Android set() has already swapped the WebView and this line is never reached.
      void deps.reload();
      return applied;
    } catch (error) {
      const failedMode = updateMode;
      const failedReason = fallbackReason;
      const attemptDurationMs = deps.now() - attemptStartedAt;
      const failedProgressive = failedMode === 'manifest_progressive' && downloadFailed;
      let filesMissingAfter: number | undefined;
      if (failedProgressive && decision.kind === 'download' && decision.manifest) {
        filesMissingAfter = await raceProbe(decision.manifest, decision.version);
        guarded(() => deps.onProbe?.(), undefined);
      }
      await deps.onAttemptFailed(attempt, error, {
        updateMode: failedMode,
        fallbackReason: failedReason,
        probe,
        attempt,
        attemptDurationMs,
        filesMissingAfter,
      });
      attempt += 1;

      let progressed = false;
      if (failedProgressive) {
        guarded(() => {
          let exhausted = false;
          if (isContentReason(classifyCapgoReason(describeUpdateFailure(error)).reasonClass)) {
            exhausted = true;
          } else if (filesMissingAfter === undefined) {
            // A lone unknown says nothing; two in a row count as no progress, or an offline device
            // would stay on the manifest route for the whole session.
            unknownStreak += 1;
            if (unknownStreak >= 2) {
              unknownStreak = 0;
              noProgressStreak += 1;
              exhausted = noProgressStreak >= 2;
            }
          } else {
            unknownStreak = 0;
            if (progressiveBaseline === undefined) {
              progressiveBaseline = filesMissingAfter;
            } else if (filesMissingAfter < progressiveBaseline) {
              progressed = true;
              progressiveBaseline = filesMissingAfter;
              noProgressStreak = 0;
            } else {
              noProgressStreak += 1;
              exhausted = noProgressStreak >= 2;
            }
          }
          if (exhausted) {
            activeManifest = undefined;
            progressiveExhausted = true;
            updateMode = 'manifest_fell_back';
            fallbackReason = 'manifest_failed';
            deps.writeProgressiveHint(null);
          }
        }, undefined);
      }

      if (
        failedMode !== 'manifest' &&
        failedMode !== 'manifest_progressive' &&
        downloadFailed &&
        !progressiveExhausted &&
        decision.kind === 'download' &&
        decision.manifest
      ) {
        const triggerManifest = decision.manifest;
        const triggerVersion = decision.version;
        guarded(() => {
          if (deps.isProgressiveTrigger(error)) {
            // Written on entry, not on success: a device that never finishes is the one to remember.
            deps.writeProgressiveHint({
              version: triggerVersion,
              until: deps.now() + PROGRESSIVE_HINT_TTL_MS,
            });
            activeManifest = triggerManifest;
            updateMode = 'manifest_progressive';
            fallbackReason = 'zip_timeout';
          }
        }, undefined);
      }

      if (activeManifest !== undefined && downloadFailed && failedMode === 'manifest') {
        manifestFailureStreak += 1;
        if (manifestFailureStreak >= 2) {
          activeManifest = undefined;
          updateMode = 'manifest_fell_back';
          fallbackReason = 'manifest_failed';
        }
      }

      if (current.kind === 'apply_installed') {
        // `list()` can name a bundle whose files were already reaped, and `set()` then rejects for
        // that id FOREVER (orphaned entries with nothing behind them).
        if (!current.url) {
          throw error;
        }
        current = {
          kind: 'download',
          url: current.url,
          version: current.version,
          checksum: current.checksum,
        };
      }
      if (!deps.shouldContinue()) {
        return { updateMode, fallbackReason, probe, attempt, attemptDurationMs, filesMissingAfter };
      }
      if (updateMode === 'manifest_progressive') {
        await runCleanup(current.version);
      }
      await deps.wait(progressed ? PROGRESSIVE_BACKOFF_MS : computeBackoffMs(attempt));
      if (!deps.shouldContinue()) {
        return { updateMode, fallbackReason, probe, attempt, attemptDurationMs, filesMissingAfter };
      }
    }
  }
};
